import {
  copilotQueryStream as apiCopilotQueryStream,
  CopilotAction,
  CopilotQueryStreamData,
  type CopilotSseEvent,
  type CopilotStreamingEvent,
} from "../api";

export interface StreamingCallbacks {
  onAction?: (action: CopilotAction) => void;
  onChunk?: (chunk: string) => void;
  onComplete?: (final: { actions?: CopilotAction[]; content: string }) => void;
  onError?: (error: Error) => void;
}

export interface StreamingOptions {
  abortController?: AbortController;
}

export const copilotQueryStream = async (
  data: {
    context?: CopilotQueryStreamData["body"]["context"];
    message: string;
    sessionId: string;
  },
  callbacks: StreamingCallbacks,
  options?: StreamingOptions,
): Promise<void> => {
  try {
    const abortSignal = options?.abortController?.signal;

    const sse = await apiCopilotQueryStream({
      body: data,
      headers: {
        Accept: "text/event-stream",
      },
      signal: abortSignal,
    });

    let accumulatedContent = "";
    const accumulatedActions: CopilotAction[] = [];
    let completed = false;

    for await (const rawEvent of sse.stream) {
      if (abortSignal?.aborted) break;

      // hey-api yields the parsed JSON from `data:`. Depending on server/client config,
      // this might be either the payload itself OR an envelope like `{ data: payload }`.
      const payload: CopilotStreamingEvent =
        rawEvent && typeof rawEvent === "object" && "data" in rawEvent
          ? (rawEvent as CopilotSseEvent).data
          : (rawEvent as CopilotStreamingEvent);

      if (payload.type === "chunk") {
        // generated type uses `text`
        accumulatedContent += payload.text;
        callbacks.onChunk?.(payload.text);
        continue;
      }

      if (payload.type === "end") {
        completed = true;
        callbacks.onComplete?.({
          actions: payload.actions ?? accumulatedActions,
          content: payload.answer ?? accumulatedContent,
        });
        break;
      }

      if (payload.type === "error") {
        const errorObj = new Error(payload.error || "Streaming error");
        callbacks.onError?.(errorObj);
        throw errorObj;
      }

      if (payload.type === "action") {
        callbacks.onAction?.(payload.action);
        accumulatedActions.push(payload.action);
      }
    }

    if (!completed && !abortSignal?.aborted) {
      callbacks.onComplete?.({
        actions: accumulatedActions,
        content: accumulatedContent,
      });
    }
  } catch (error) {
    console.error("error", error);
    const errorObj =
      error instanceof Error ? error : new Error("Streaming request failed");
    callbacks.onError?.(errorObj);
    throw errorObj;
  }
};
